import React from 'react';
import AnimalAvatar from './AnimalAvatar';
import './PresenceStack.css';

// Status-dot color cutoffs (ms). Mirrors the spec from F4.2 / F5.3.
const FRESH_MS = 30_000; // < 30s → green
const IDLE_MS = 180_000; // < 3 min → yellow; beyond → gray

function statusFor(lastSeen, now) {
  if (!lastSeen) return 'fresh';
  const delta = now - lastSeen;
  if (delta < FRESH_MS) return 'fresh';
  if (delta < IDLE_MS) return 'idle';
  return 'stale';
}

/**
 * PresenceStack — overlapping avatars + optional "+N" chip.
 *
 * Props:
 *   - members: Array<{ id, handle, avatarSeed, lastSeen }>
 *   - max: number — visible avatars before collapsing into the "+N" chip
 *   - onClick: () => void — clicking the stack opens the members panel
 *   - className: optional class for the wrapper
 */
function PresenceStack({
  members = [],
  max = 5,
  onClick,
  className = '',
}) {
  const now = Date.now();
  const visible = members.slice(0, max);
  const extra = members.length - visible.length;
  const interactive = typeof onClick === 'function';

  const wrapClass =
    `glacier-presence-stack${interactive ? ' is-interactive' : ''}` +
    (className ? ` ${className}` : '');

  const content = (
    <>
      {visible.map((m) => {
        const status = statusFor(m.lastSeen, now);
        return (
          <span
            key={m.id}
            className="glacier-presence-stack__item"
            data-status={status}
            title={m.handle || m.id}
          >
            <AnimalAvatar seed={m.avatarSeed || m.id} size={28} ring />
            <span
              className="glacier-presence-stack__dot"
              data-status={status}
              aria-hidden="true"
            />
          </span>
        );
      })}
      {extra > 0 && (
        <span
          className="glacier-presence-stack__more"
          aria-label={`${extra} more`}
        >
          +{extra}
        </span>
      )}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        className={wrapClass}
        onClick={onClick}
        aria-label={`Show participants (${members.length} online)`}
        data-testid="presence-stack"
      >
        {content}
      </button>
    );
  }
  return (
    <div
      className={wrapClass}
      aria-label={`${members.length} members online`}
      data-testid="presence-stack"
    >
      {content}
    </div>
  );
}

export default PresenceStack;
export const __test__ = { statusFor };
